import { useEffect } from "react";
import SEO from "./SEO";

const Refund = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
     <div className="max-w-4xl mx-auto px-4 py-10 text-gray-800 shadow-2xl m-10">
       <SEO
      title="Refund & Return Policy | Pufframa – Nicotine Product Orders"
      description="Review Pufframa's 30-day refund and return policy for nicotine-related products. Learn about return eligibility, non-returnable items, partial refunds, and exchange options."
      url="https://pufframa.com/refund-policy"
      keywords="nicotine refund policy, return nicotine products, pufframa return, exchange policy, refund terms"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Refund and Return Policy",
        description:
          "Refund and return terms for nicotine purchases made on Pufframa. Includes eligibility, exemptions, and contact information.",
        url: "https://pufframa.com/refund-policy"
      }}
    />
      <h1 className="text-3xl font-bold mb-6 text-center">Refund and Returns Policy</h1>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Overview</h2>
        <p>
          Our refund and returns policy lasts <strong>30 days</strong>. If 30 days have passed since your purchase, we can’t offer you a full refund or exchange.
        </p>
        <p className="mt-2">
          To be eligible for a return, your item must be unused and in the same condition that you received it. It must also be in the original packaging.
        </p>
        <p className="mt-2">
          Several types of goods are exempt from being returned:
        </p>
        <ul className="list-disc list-inside ml-4 mt-2">
          <li>Perishable goods (food, flowers, newspapers, magazines)</li>
          <li>Intimate or sanitary goods</li>
          <li>Hazardous materials</li>
          <li>Flammable liquids or gases</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Additional Non-returnable Items</h2>
        <ul className="list-disc list-inside ml-4">
          <li>Gift cards</li>
          <li>Downloadable software products</li>
          <li>Some health and personal care items</li>
        </ul>
        <p className="mt-2">A receipt or proof of purchase is required to complete your return.</p>
        <p className="mt-2 font-medium text-red-600">Do not send your purchase back to the manufacturer.</p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Partial Refunds (If Applicable)</h2>
        <ul className="list-disc list-inside ml-4">
          <li>Books with obvious signs of use</li>
          <li>Opened CDs, DVDs, VHS tapes, software, video games, cassette tapes, or vinyl records</li>
          <li>Items not in original condition, damaged, or missing parts not due to our error</li>
          <li>Items returned more than 30 days after delivery</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Refunds</h2>
        <p>
          Once your return is received and inspected, we’ll notify you via email about the approval or rejection of your refund.
        </p>
        <p className="mt-2">
          If approved, the refund will be processed and a credit will be applied to your original method of payment within a few days.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Late or Missing Refunds</h2>
        <ul className="list-disc list-inside ml-4">
          <li>Check your bank account again</li>
          <li>Contact your credit card company</li>
          <li>Contact your bank</li>
        </ul>
        <p className="mt-2">
          If you’ve done all of this and still haven’t received your refund, please contact us at{" "}
          <a href="mailto:pufframa@gmail.com" className="text-blue-600 underline">pufframa@gmail.com</a>.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Sale Items</h2>
        <p>Only regular priced items may be refunded. Sale items are non-refundable.</p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Exchanges</h2>
        <p>
          We only replace items if they are defective or damaged. For exchanges, email us at{" "}
          <a href="mailto:pufframa@gmail.com" className="text-blue-600 underline">pufframa@gmail.com</a>.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Gifts</h2>
        <p>
          If the item was marked as a gift and sent directly to you, you’ll receive a gift credit once the return is processed.
        </p>
        <p className="mt-2">
          If the item was not marked as a gift or was shipped to the gift giver, the refund will go to the gift giver.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Shipping Returns</h2>
        <p>
          You are responsible for paying shipping costs for returning your item. Shipping costs are non-refundable.
        </p>
        <p className="mt-2">
          For expensive items, consider using a trackable shipping service or purchasing shipping insurance. We don’t guarantee we’ll receive your returned item.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Need Help?</h2>
        <p>
          Contact us at{" "}
          <a href="mailto:pufframa@gmail.com" className="text-blue-600 underline">pufframa@gmail.com</a>{" "}
          for questions related to refunds and returns.
        </p>
      </section>
    </div>
  );
};

export default Refund;
